<div id="head"></div>
<h1 class="sr-only" *ngIf="def">{{ def.heading }} - Convert {{ def.fromLabel }} Color Codes to {{ def.toLabel }}</h1>

<!-- Hero: a purpose-built converter card on the same color-derived banded
     backdrop as the color page. Left = swatch + editable hex; right = the RGB
     result + copy + channel bars. -->
<div *ngIf="dataReady && def"
     class="default-section initial-section metallic-section no-buttons"
     data-section="top"
     [style.--hero-bg]="heroBackground">
  <!-- the converter's name on top and the conversion under it, as the color tool shows its colour's name
       (owner 2026-10-12) -->
  <div class="cp-hero-head">
    <p class="tools-title" aria-hidden="true">{{ def.heading }}</p>
    <p class="tools-subtitle">{{ sourceLabel }} is {{ targetLabel }}</p>
  </div>
  <app-converter-hero [hex]="hex"
                      [fromLabel]="def.fromLabel"
                      [toLabel]="def.toLabel"
                      (colorChange)="onWidgetColor( $event )"></app-converter-hero>
</div>

<!-- a chip per section under the hero, one line that slides sideways and stays under the nav (owner 2026-10-12) -->
<nav class="cp-chipbar" appStuck *ngIf="dataReady && def" aria-label="Converter sections">
  <div class="cp-chipbar-row">
    <a *ngFor="let s of pageSections" class="pl-chip" [class.on]="activeSection === s.key" [attr.href]="'#sec-' + s.key"
       (click)="$event.preventDefault(); scrollToSection( s.key )">
      <span>{{ s.label }}</span>
    </a>
  </div>
</nav>

<!-- ===================== Body: every section as a row of two cards (owner 2026-10-12) ===================== -->
<div class="cp-body cp-boxes" *ngIf="dataReady && def">

  <div class="cp-content">
    <!-- Formats: the current color written every common way of the output
         notation (RGB or HEX depending on direction), each copyable. -->
    <section class="cp-section" id="sec-formats" data-section="formats">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background-color]="hex"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">{{ def.toLabel }} Formats</h2>
      <p class="cp-section-lead">The same color written every common {{ def.toLabel }} way, ready to copy</p>
      </header>
      <div class="cp-main">

      <div class="conv-fmt">
        <div class="conv-fmt-row" *ngFor="let f of rgbFormats">
          <span class="conv-fmt-label">{{ f.label }}</span>
          <span class="conv-fmt-value">{{ f.value }}</span>
          <button type="button" class="conv-fmt-copy" (click)="copy( f.value, f.key )">
            {{ copiedKey === f.key ? 'Copied' : 'Copy' }}
          </button>
        </div>
      </div>
      </div>
    </section>

    <!-- How it works: split the color into its three channels, then show the
         base-16 arithmetic — hex pair to 0-255 value, or the reverse. -->
    <section class="cp-section" id="sec-howitworks" data-section="howitworks">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background-color]="hex"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">How {{ def.fromLabel }} to {{ def.toLabel }} Works</h2>
      <p class="cp-section-lead" *ngIf="!isReverse && def.family === 'rgb'">Six hex digits are really three pairs: two for red, two for green, two for blue</p>
      <p class="cp-section-lead" *ngIf="isReverse && def.family === 'rgb'">Each RGB channel becomes one two digit hex pair: red, then green, then blue</p>
      <p class="cp-section-lead" *ngIf="!isReverse && def.family !== 'rgb'">Six hex digits split into red, green and blue; {{ def.components }} are computed from those three channels</p>
      <p class="cp-section-lead" *ngIf="isReverse && def.family !== 'rgb'">{{ def.components }} first become red, green and blue; each channel then becomes one two digit hex pair</p>
      </header>
      <div class="cp-main">

      <!-- The from value (a box split into its channels) flows into an arrow and
           then the to value split the SAME way, so both sides of the conversion
           read as color-coded channels with their labels underneath. -->

      <!-- The hex value as color-coded pairs (# 54 9E FF, RED / GREEN / BLUE). -->
      <ng-template #hexSplit>
        <div class="conv-split">
          <span class="conv-split-hash">#</span>
          <span class="conv-split-cell" *ngFor="let c of workedChannels">
            <span class="conv-split-pair" [style.color]="c.tint">{{ c.hexPair }}</span>
            <span class="conv-split-cap" [style.color]="c.tint">{{ c.channel }}</span>
          </span>
        </div>
      </ng-template>

      <!-- The rgb() value as color-coded channels (rgb( 84, 158, 255 )). -->
      <ng-template #rgbSplit>
        <div class="conv-split">
          <span class="conv-split-hash">rgb(</span>
          <ng-container *ngFor="let c of workedChannels; let i = index">
            <span class="conv-split-hash" *ngIf="i > 0">,</span>
            <span class="conv-split-cell">
              <span class="conv-split-pair" [style.color]="c.tint">{{ c.dec }}</span>
              <span class="conv-split-cap" [style.color]="c.tint">{{ c.channel }}</span>
            </span>
          </ng-container>
          <span class="conv-split-hash">)</span>
        </div>
      </ng-template>

      <!-- The hsl/hsv/hwb/cmyk/oklch/oklab value as color-coded channels with labels.
           hwb(), oklch() and oklab() are space-separated in CSS, so they drop commas. -->
      <ng-template #notationSplit>
        <div class="conv-split">
          <span class="conv-split-hash">{{ notationPrefix }}</span>
          <ng-container *ngFor="let part of otherParts; let i = index">
            <span class="conv-split-hash" *ngIf="i > 0 && def.family !== 'hwb' && def.family !== 'oklch' && def.family !== 'oklab' && def.family !== 'lab' && def.family !== 'lch' && def.family !== 'p3'">,</span>
            <span class="conv-split-cell">
              <span class="conv-split-pair" [style.color]="part.tint">{{ part.display }}</span>
              <span class="conv-split-cap" [style.color]="part.tint">{{ part.cap }}</span>
            </span>
          </ng-container>
          <span class="conv-split-hash">)</span>
        </div>
      </ng-template>

      <div class="conv-flow">
        <!-- From: hex on forward pages, the notation on reverse pages. -->
        <ng-container *ngTemplateOutlet="isReverse ? ( def.family === 'rgb' ? rgbSplit : notationSplit ) : hexSplit"></ng-container>
        <span class="conv-flow-arrow" aria-hidden="true">&rarr;</span>
        <!-- To: the opposite of the from side. -->
        <ng-container *ngTemplateOutlet="isReverse ? hexSplit : ( def.family === 'rgb' ? rgbSplit : notationSplit )"></ng-container>
      </div>

      <p class="conv-how-text" *ngIf="!isReverse">
        Each pair is a base 16 (hexadecimal) number. To read it in plain decimal, multiply the
        first digit by 16 and add the second. The result always lands between 0 (<code>00</code>)
        and 255 (<code>FF</code>) &mdash; exactly one RGB channel.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'rgb'">
        Each channel is a decimal number between 0 and 255. Divide it by 16: the quotient is the
        first hex digit and the remainder is the second. Digits above 9 use letters, so 10 is
        <code>A</code> and 15 is <code>F</code> &mdash; which is why 255 becomes <code>FF</code>.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'hsl'">
        The hue angle picks the position on the color wheel, saturation how vivid it is, and
        lightness how close it sits to black or white. Those three resolve to the red, green and
        blue channels below; each channel then divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'hsv'">
        The hue angle picks the position on the color wheel, saturation how vivid it is, and
        value how bright the strongest channel is. Those three resolve to the red, green and
        blue channels below; each channel then divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'hsb'">
        The hue angle picks the position on the color wheel, saturation how vivid it is, and
        brightness how strong the strongest channel is. Those three resolve to the red, green
        and blue channels below; each channel then divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'hwb'">
        The hue angle picks the position on the color wheel, whiteness how much white is mixed
        in, and blackness how much black. Those three resolve to the red, green and blue
        channels below; each channel then divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'cmyk'">
        Each value is an ink coverage: how much cyan, magenta and yellow the paper gets, with key
        as the shared black plate. Removing each ink and the key from the paper's full brightness
        resolves the red, green and blue channels below; each channel then divides by 16 into one
        two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'oklch'">
        Lightness sets how light the color looks, chroma how colorful it is, and hue its angle on
        the perceptual color wheel. Working back through OKLAB, those three resolve to the red, green
        and blue channels below; each channel then divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'oklab'">
        Lightness sets how light the color looks, while a runs from green to red and b from blue to
        yellow. Those three resolve to the red, green and blue channels below; each channel then
        divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'lab'">
        Lightness sets how light the color looks, while a runs from green to red and b from blue to
        yellow. Working back through CIE XYZ, those three resolve to the red, green and blue channels
        below; each channel then divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'lch'">
        Lightness sets how light the color looks, chroma how colorful it is, and hue its angle on the
        color wheel. Those polar values unwind into CIELAB, then through CIE XYZ into the red, green
        and blue channels below; each channel then divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'hsl'">
        From those channels, lightness is the average of the largest and smallest, saturation is
        how far they spread, and hue is the angle the dominant channel points to on the color wheel
        &mdash; which is how <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'hsv'">
        From those channels, value is simply the largest one, saturation is the spread relative to
        that largest channel, and hue is the angle the dominant channel points to on the color wheel
        &mdash; which is how <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'hsb'">
        From those channels, brightness is simply the largest one, saturation is the spread relative
        to that largest channel, and hue is the angle the dominant channel points to on the color
        wheel &mdash; which is how <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'hwb'">
        From those channels, whiteness is simply the smallest one, blackness is how far the largest
        falls short of full, and hue is the angle the dominant channel points to on the color wheel
        &mdash; which is how <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'cmyk'">
        From those channels, the key is how far the brightest one falls short of full (the black
        every ink would otherwise repeat), and each ink covers whatever its own channel still
        leaves dark &mdash; which is how <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'oklch'">
        From those channels, OKLCH works in the perceptual OKLAB space: lightness tracks how bright
        the color looks, chroma how far it sits from grey, and hue its angle on the color wheel
        &mdash; which is how <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'oklab'">
        From those channels, OKLAB reads the same perceptual space on two signed axes: lightness for
        how bright it looks, a for green to red, and b for blue to yellow
        &mdash; which is how <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'lab'">
        From those channels, CIELAB works through CIE XYZ: lightness for how bright it looks, a for
        green to red, and b for blue to yellow
        &mdash; which is how <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'lch'">
        From those channels, CIELCH reads CIELAB in polar form through CIE XYZ: lightness for how
        bright it looks, chroma for how colorful it is, and hue for its angle on the color wheel
        &mdash; which is how <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'p3'">
        Display P3 is red, green and blue again, but measured against wider primaries than sRGB.
        The three channels are decoded from their transfer curve, re-mixed into sRGB light and
        clamped to the smaller gamut; each channel then divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'p3'">
        From those channels, Display P3 re-mixes the same light against its wider red and green
        primaries, so a saturated sRGB color lands a little closer to the middle of the P3 range
        &mdash; which is how <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'xyz'">
        X, Y and Z are the CIE tristimulus values, the device independent measure of a color with Y
        as its luminance. They un-mix through the XYZ to sRGB matrix into the red, green and blue
        channels below; each channel then divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'xyz'">
        From those channels, CIE XYZ linearizes the light and weights each channel into three
        tristimulus values, Y being the luminance, with white at Y = 100 &mdash; which is how
        <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'yxy'">
        Y is the luminance and x, y are the chromaticity coordinates on the CIE 1931 diagram. They
        rebuild the XYZ tristimulus values, which un-mix into the red, green and blue channels
        below; each channel then divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'yxy'">
        From those channels, CIE Yxy keeps the luminance Y of CIE XYZ and normalizes X and Y by the
        sum of all three into the chromaticity coordinates x and y &mdash; which is how
        <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'hunter'">
        Hunter Lab is the 1958 opponent-color space that CIELAB later refined: L from the square
        root of the luminance, a and b from the red&ndash;green and yellow&ndash;blue differences. They
        rebuild the XYZ tristimulus values, which un-mix into the red, green and blue channels
        below; each channel then divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'hunter'">
        From those channels, Hunter Lab goes through CIE XYZ and takes L as ten times the square
        root of Y, then a and b from the X&ndash;Y and Y&ndash;Z differences scaled by that root &mdash;
        which is how <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <p class="conv-how-text" *ngIf="isReverse && def.family === 'luv'">
        CIELUV (CIE 1976 L*u*v*) is the CIELAB sibling built for additive light: L is the same
        lightness, while u and v measure how far the color sits from the D65 white on the u&prime;v&prime;
        chromaticity diagram. They rebuild the XYZ tristimulus values, which un-mix into the red,
        green and blue channels below; each channel then divides by 16 into one two digit hex pair.
      </p>

      <p class="conv-how-text" *ngIf="!isReverse && def.family === 'luv'">
        From those channels, CIELUV goes through CIE XYZ: L is the CIELAB lightness, then u and v
        scale the distance from the white point on the u&prime;v&prime; chromaticity diagram by 13 times L,
        which is how <strong>{{ rgbString }}</strong> reads as <strong>{{ otherString }}</strong>.
      </p>

      <!-- HSL family: the worked derivation, every intermediate value with the
           current color's real numbers. Reverse shows it BEFORE the base-16
           table (hsl -> rgb happens first), forward AFTER it (rgb -> hsl). -->
      <div class="conv-math conv-hsl-steps" *ngIf="isReverse && def.family !== 'rgb'">
        <div class="conv-math-row" *ngFor="let step of otherSteps">
          <span class="conv-math-ch">{{ step.label }}</span>
          <span class="conv-math-expr">{{ step.expr }}</span>
        </div>
      </div>

      <p class="conv-how-text" *ngIf="isReverse && def.family !== 'rgb'">
        Those channels are <strong>{{ rgbString }}</strong>; each one now divides by 16 into its hex pair:
      </p>

      <div class="conv-math">
        <div class="conv-math-row" *ngFor="let c of workedChannels">
          <span class="conv-math-ch">{{ c.channel }}</span>
          <span class="conv-math-expr" *ngIf="!isReverse">
            <b>{{ c.hexPair }}</b>
            <span class="conv-math-op">=</span> {{ c.hi }} &times; 16 + {{ c.lo }}
            <span class="conv-math-op">=</span> <b>{{ c.dec }}</b>
          </span>
          <span class="conv-math-expr" *ngIf="isReverse">
            <b>{{ c.dec }}</b> &divide; 16
            <span class="conv-math-op">=</span> {{ c.hi }} remainder {{ c.lo }}
            <span class="conv-math-op">&rarr;</span> <b>{{ c.hexPair }}</b>
          </span>
        </div>
      </div>

      <p class="conv-how-text" *ngIf="!isReverse && def.family !== 'rgb'">
        With the channels in hand, {{ def.components }} each come from their own step:
      </p>

      <div class="conv-math conv-hsl-steps" *ngIf="!isReverse && def.family !== 'rgb'">
        <div class="conv-math-row" *ngFor="let step of otherSteps">
          <span class="conv-math-ch">{{ step.label }}</span>
          <span class="conv-math-expr">{{ step.expr }}</span>
        </div>
      </div>

      <p class="conv-work-note" *ngIf="!isReverse && def.family === 'rgb'">Put the three channels together and <strong>{{ outHex }}</strong> is <strong>{{ rgbString }}</strong>.</p>
      <p class="conv-work-note" *ngIf="isReverse && def.family === 'rgb'">Put the three pairs together and <strong>{{ rgbString }}</strong> is <strong>{{ outHex }}</strong>.</p>
      <p class="conv-work-note" *ngIf="!isReverse && def.family !== 'rgb'">Put it all together and <strong>{{ outHex }}</strong> is <strong>{{ rgbString }}</strong>, which reads as <strong>{{ otherString }}</strong>.</p>
      <p class="conv-work-note" *ngIf="isReverse && def.family !== 'rgb'"><strong>{{ otherString }}</strong> is <strong>{{ rgbString }}</strong>, and the three pairs together make <strong>{{ outHex }}</strong>.</p>
      </div>
    </section>

    <!-- More Color Tools (owner 2026-10-22): the Color Picker, above Related Themes -->
    <app-more-tools sectionKey="more" title="More Color Tools" lead="Pick any color on your screen and read it the same way, right from your menu bar"
                    [app]="colorPicker" [source]="moreToolsSource"></app-more-tools>

    <!-- Related Themes: same box design as the color page. -->
    <section class="cp-section" id="sec-themes" data-section="themes" *ngIf="relatedThemeList.length">
      <header class="cp-head">
      <h2 class="cp-h2">Related Themes</h2>
      <p class="cp-section-lead">IDE themes whose palette sits closest to this color</p>
      </header>
      <div class="cp-main">

      <div class="cp-theme-grid">
        <app-theme-card *ngFor="let t of relatedThemeList"
                        [product]="t.product"
                        [category]="t.category"
                        [compact]="false"
                        [tile]="false"
                        [selectable]="false"
                        [clickable]="true"
                        [screenshotMode]="false">
        </app-theme-card>
      </div>
      </div>
    </section>

    <!-- From The Blog: same poster cards as the color page. -->
    <section class="cp-section" id="sec-blog" data-section="blog">
      <header class="cp-head">
      <h2 class="cp-h2">From The Blog</h2>
      <p class="cp-section-lead">Practical reads on how colors are built and combined</p>
      </header>
      <div class="cp-main">

      <div class="cp-blog-cards">
        <a class="cp-blog-card" *ngFor="let post of blogCards" [routerLink]="post.url"
           [style.--card-accent]="post.coverAccent || null">
          <img class="cp-blog-bg" [src]="post.cover" [alt]="post.title" loading="lazy" decoding="async">
          <span class="cp-blog-scrim"></span>
          <svg class="cp-blog-part" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 210 297" aria-hidden="true">
            <path d="M 31.496131,83.498219 C 17.281586,96.049685 13.920954,122.9803 24.61827,138.63631 v 0 c 8.67477,16.8641 33.95747,26.73174 51.762024,20.2132 v 0 l 54.361716,54.36558 -83.532627,83.53647 60.446297,8e-5 84.02015,-83.53655 -84.82866,-84.83262 C 113.3695,110.57782 103.49808,85.295107 86.634028,76.620298 v 0 C 80.390777,72.357466 72.362085,70.323536 64.161412,70.323536 v 0 c -12.368001,0 -25.118271,4.626039 -32.665281,13.174683"/>
          </svg>
          <span class="cp-blog-content">
            <span class="cp-blog-cat">{{ post.categoryLabel }}</span>
            <span class="cp-blog-title">{{ post.title }}</span>
            <span class="cp-blog-meta">{{ post.dateLabel }} &middot; {{ post.readingTimeMinutes }} min read</span>
          </span>
        </a>
      </div>
      </div>
    </section>

    <!-- Questions close the page, after the blog (the how-it-works of a converter is its own section above) -->
    <app-tool-about title="Questions" [faq]="faq" [accent]="hex" [ratingKey]="def.slug" [ratingName]="'the ' + def.fromLabel + ' to ' + def.toLabel + ' Converter'"></app-tool-about>

  </div>
</div>
